// src/contexts/authHelpers.ts
//
// Types, guards, and pure helpers used by AuthContext. Extracted to keep
// AuthContext.tsx under the project's 500-line file limit.

import { jwtDecode } from "jwt-decode";
import { track, identifyUser } from "../utils/analytics";
import type { AuthTier } from "../config/authConfig";
import { FORCE_LEGACY_SSO } from "../config/authConfig";
import { SilentTimeoutError, InteractionRequiredFallbackError } from "../services/msalAuthService";

export interface UserInfo {
  name: string;
  email: string;
}

/**
 * Office.js auth error shape. The library attaches a numeric `code` (13001,
 * 13002, …) and an error `message`; we read `code` to branch on specific
 * cases. Anything that's not an Office.js error is treated as a generic Error.
 */
export interface OfficeAuthError {
  code?: number;
  message?: string;
}

/**
 * Narrow `unknown` to the Office.js auth-error shape. Returns an empty object
 * if the input isn't object-like — callers read `.code` defensively.
 */
export function asOfficeAuthError(error: unknown): OfficeAuthError {
  if (typeof error === "object" && error !== null) {
    return error as OfficeAuthError;
  }
  return {};
}

/**
 * Detect supported auth tier based on Office requirement sets.
 * NAA (NestedAppAuth 1.1) is preferred; falls back to Legacy SSO (IdentityAPI 1.3).
 *
 * Respects the FORCE_LEGACY_SSO kill-switch: if the env flag is set we skip
 * NAA detection entirely, allowing a runtime rollback without a code change.
 */
export function detectAuthTier(): AuthTier {
  const requirements = Office.context.requirements;
  if (!FORCE_LEGACY_SSO && requirements?.isSetSupported?.("NestedAppAuth", "1.1")) return "naa";
  if (requirements?.isSetSupported?.("IdentityAPI", "1.3")) return "legacy_sso";
  return "none";
}

/**
 * Human-readable message when no auth tier is supported. We try to name the
 * specific host (e.g. Word for iPhone) so the user gets actionable guidance
 * instead of a generic "not supported" dead end.
 */
export function describeUnsupportedPlatform(): string {
  try {
    const host = Office.context.host;
    const platform = Office.context.platform;
    const PlatformType = Office.PlatformType;
    if (platform === PlatformType.iOS) {
      // iOS covers both iPad and iPhone; NAA only supports iPad on recent Office builds.
      return (
        "Authentication is not supported on Word for iPhone. " +
        "Please use Word on iPad, Word on the web (office.com), or Word Desktop."
      );
    }
    if (platform === PlatformType.Android) {
      return (
        "Authentication is not supported on Word for Android. " +
        "Please use Word on the web or Word Desktop."
      );
    }
    if (host === Office.HostType.Word && platform === PlatformType.PC) {
      // Desktop Word that reported neither NAA nor IdentityAPI — likely an old
      // Office build.
      return (
        "Your version of Word is too old to sign in. " +
        "Please update Office (File → Account → Update Options → Update Now) and reopen the add-in."
      );
    }
  } catch {
    /* fall through to default */
  }
  return "Authentication is not supported on this platform.";
}

/**
 * Extract user info from a decoded JWT token.
 * Handles both v2.0 (preferred_username) and v1.0 (unique_name) formats.
 */
export function extractUserInfoFromToken(token: string): UserInfo {
  const decoded = jwtDecode(token);
  const name = ((decoded as Record<string, unknown>)?.name as string) || "";
  const email =
    ((decoded as Record<string, unknown>)?.preferred_username as string) ||
    ((decoded as Record<string, unknown>)?.unique_name as string) ||
    "";
  return { name, email };
}

/** Exponential backoff for throttling (code 13013), linear otherwise. */
export function computeBackoffDelay(
  attempt: number,
  baseDelayMs: number,
  isThrottled: boolean
): number {
  return isThrottled ? baseDelayMs * Math.pow(2, attempt - 1) : baseDelayMs * attempt;
}

/**
 * Choose a user-facing error message. If the underlying error already reads
 * like human-friendly text (starts with "Please …"), surface it; otherwise
 * fall back to a generic message directing the user to sign in.
 */
export function deriveUserFacingLegacyError(errorMessage: string): string {
  if (errorMessage.includes("Please")) return errorMessage;
  return (
    "Authentication failed, but you can still use Contract.Space with limited functionality. " +
    "Please ensure you are signed in with your Office 365 work account for full features."
  );
}

/**
 * Classify a caught NAA error into a short telemetry reason code. Uses
 * `instanceof` on our typed errors — stable across copy edits, unlike regex
 * on `.message` which was how this worked before the CS-433 refactor.
 */
export function classifyNaaFailure(error: unknown): string {
  if (error instanceof SilentTimeoutError) return "msal_silent_timeout";
  if (error instanceof InteractionRequiredFallbackError) return "msal_interaction_required";
  return "msal_token_acquisition_failed";
}

/**
 * Extract MSAL's diagnostic fields from a caught error. MSAL AuthError exposes
 * `correlationId`, `errorCode`, and `subError` which let engineers cross-
 * reference Azure sign-in logs. Shape varies across MSAL minor versions, so
 * read defensively.
 *
 * `msalErrorName` (the error's class name, e.g. `ServerError`,
 * `ClientAuthError`, `NestedAppAuthError`) is the load-bearing one for NAA on
 * Windows: when the Office host's NAA bridge refuses to broker a token, MSAL's
 * `fromBridgeError` maps a Persistent/Transient host failure to a
 * `ServerError(code, description)` whose `code`/`description` are EMPTY — so
 * `errorCode`/`subError`/`message` are all blank and only the class name
 * survives. Capturing it turns the "empty error" telemetry into a usable signal.
 */
export interface MsalErrorFields {
  correlationId?: string;
  msalErrorCode?: string;
  msalSubError?: string;
  msalErrorName?: string;
}
export function extractMsalErrorFields(error: unknown): MsalErrorFields {
  if (!error || typeof error !== "object") return {};
  const e = error as Record<string, unknown>;
  return {
    correlationId: typeof e.correlationId === "string" ? e.correlationId : undefined,
    msalErrorCode: typeof e.errorCode === "string" ? e.errorCode : undefined,
    msalSubError: typeof e.subError === "string" ? e.subError : undefined,
    msalErrorName: typeof e.name === "string" ? e.name : undefined,
  };
}

// True once `app:opened` has been emitted for this page load. Token
// re-acquisitions (silent-refresh failures, legacy refreshes) re-run the
// identify path below, but they are refreshes of the same app session - only
// the FIRST successful sign-in of a page load counts as "the user opened the
// add-in". Refresh activity is tracked separately as auth:token_refreshed.
let appOpenedEmitted = false;

/** Test-only: reset the once-per-page-load guard between tests. */
export function resetAppOpenedForTests(): void {
  appOpenedEmitted = false;
}

/**
 * PostHog identify + one-time "app:opened" capture. Keeps PII (name/email)
 * only in the identify call, not in the event payload — a runtime log-level
 * or PostHog-filter rule shouldn't be able to expose emails through events.
 */
export function identifyAndEmitAppOpened(
  user: UserInfo,
  authTier: AuthTier,
  exp: number | undefined,
  attempt?: number
): void {
  if (user.email) {
    identifyUser(user.email, { name: user.name, email: user.email });
  }
  if (appOpenedEmitted) return;
  appOpenedEmitted = true;
  track("app:opened", {
    auth_tier: authTier,
    token_expires_at: exp ? new Date(exp * 1000).toISOString() : undefined,
    ...(attempt !== undefined ? { attempt } : {}),
  });
}
